import { createFileRoute, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { AppShell } from "@/components/layout/AppShell";
import { apiRequest, AuthRequiredError } from "@/lib/api";
import { useCurrentUser } from "@/lib/hooks/auth";
import { canAccessPath } from "@/lib/route-access";
import type { UserRole } from "@/lib/schemas/users";
import type { SessionData } from "@/lib/session";

export const Route = createFileRoute("/_app")({
  // Auth guard server-side via `beforeLoad`: chiama /auth/sessions e se
  // 401 redirige a /login preservando l'URL d'origine in return_to.
  beforeLoad: async ({ location }) => {
    try {
      await apiRequest<SessionData[]>("/auth/sessions");
    } catch (err) {
      if (err instanceof AuthRequiredError) {
        throw redirect({
          to: "/login",
          search: { return_to: location.pathname },
        });
      }
      throw err;
    }
  },
  component: AppLayout,
});

function AppLayout(): JSX.Element {
  // La query e' già stata fatta in beforeLoad e cached da TanStack Query
  // (stessa key di useCurrentSession). Qui leggiamo dalla cache senza
  // network call addizionale.
  const { data } = useQuery({
    queryKey: ["auth", "sessions", "current"],
    queryFn: async () => {
      const sessions = await apiRequest<SessionData[]>("/auth/sessions");
      return sessions[0] ?? null;
    },
    staleTime: 60_000,
  });

  // WAPT 2026-09 V11: le rotte amministrative si aprivano per chiunque fosse
  // autenticato, anche se assenti dal menu. I dati restavano filtrati dal BE,
  // ma la schermata non doveva comparire: qui riportiamo alla dashboard chi
  // arriva per URL diretto su una pagina che il suo ruolo non contempla.
  //
  // Non e' un controllo di sicurezza — quello e' e resta lato backend — quindi
  // finche' /users/me non ha risposto non blocchiamo nulla: un redirect basato
  // su dati non ancora caricati butterebbe fuori anche un utente legittimo.
  const me = useCurrentUser();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();

  const allowed =
    me.data === undefined
      ? true
      : canAccessPath(pathname, {
          role: me.data.role as UserRole,
          isAccountAdmin: me.data.is_account_admin === true,
          brandingDelegated: me.data.branding_delegated === true,
        });

  useEffect(() => {
    if (!allowed) {
      void navigate({ to: "/", replace: true });
    }
  }, [allowed, navigate]);

  if (!allowed) return <></>;

  return <AppShell session={data ?? null} />;
}
